import React, { useEffect, useMemo, useState } from "react";
import {
  Stack,
  Box,
  Typography,
  alpha,
  useTheme,
  Chip,
  Button,
} from "@mui/material";
import { Formik, Form, FormikProps } from "formik";
import * as Yup from "yup";

// Icons
import PaymentIcon from "@mui/icons-material/Payment";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import MpesaIcon from "@mui/icons-material/PhoneAndroid";
import LinkIcon from "@mui/icons-material/Link";

// Components
import AppModal from "../common/AppModal";
import FormAlert from "../common/FormAlert";
import FormSelectField from "../common/FormSelectField";
import FormTextField from "../common/FormTextField";
import SubmitButton from "../buttons/SubmitButton";
import StkPushModal from "./StkPushModal";
import AllocatePaymentModal from "./AllocatePaymentModal";

// Redux & Utils
import { useAppDispatch, useAppSelector } from "../../redux/store";
import { getPaymentMethods } from "../../redux/actions/paymentMethodActions";
import { PaymentRequestDto } from "../../shared/dtos/payment/PaymentRequestDto";
import { formatCurrency, formatPlainNumber } from "../../utils/appUtils";

export type PaymentEntityType =
  "Debt" | "Invoice" | "Quotation" | "Purchase Order";

interface RecordPaymentModalProps {
  open: boolean;
  onClose: () => void;
  entityId: string;
  entityType: PaymentEntityType;
  documentNumber: string;
  balance: number;
  currencyCode?: string;
  customerId?: string; // Needed to auto-fetch phone for STK Push
  loading: boolean;
  error: string | null;
  onSubmitPayment: (entityId: string, payload: PaymentRequestDto) => void;
}

interface PaymentFormValues {
  paymentMethodId: string;
  amount: number | "";
  referenceCode: string;
  unallocatedPaymentId: string | null;
  unallocatedMaxAmount: number | null;
}

const RecordPaymentModal: React.FC<RecordPaymentModalProps> = ({
  open,
  onClose,
  entityId,
  entityType,
  documentNumber,
  balance,
  currencyCode = "KES",
  customerId,
  loading,
  error,
  onSubmitPayment,
}): React.JSX.Element => {
  const dispatch = useAppDispatch();
  const theme = useTheme();

  // --- REDUX STATE ---
  const { items: paymentMethods } = useAppSelector(
    (state) => state.paymentMethod,
  );
  const { branchSettings } = useAppSelector((state) => state.settings);
  const { items: customers } = useAppSelector((state) => state.customer);

  // --- SUB-MODAL STATE ---
  const [stkModalOpen, setStkModalOpen] = useState<boolean>(false);
  const [allocateModalOpen, setAllocateModalOpen] = useState<boolean>(false);

  useEffect(() => {
    if (!paymentMethods || paymentMethods.length === 0) {
      dispatch(getPaymentMethods());
    }
  }, [dispatch, paymentMethods]);

  const pmOptions = useMemo(
    () => paymentMethods.map((pm) => ({ value: pm.id, label: pm.name })),
    [paymentMethods],
  );

  const getCustomerPhone = (): string => {
    if (!customerId) return "";
    const c = customers.find((cust) => cust.id === customerId);
    return c?.primaryContact || "";
  };

  // --- VALIDATION & INITIAL VALUES ---
  const paymentSchema = Yup.object().shape({
    paymentMethodId: Yup.string().required("Payment method required"),
    amount: Yup.number()
      .transform((value, originalValue) =>
        typeof originalValue === "string" ? Number(originalValue) : value,
      )
      .required("Amount is required")
      .positive("Must be greater than 0")
      .test("max-allowed", "Amount exceeds limits", function (value) {
        const { unallocatedMaxAmount } = this.parent;
        const maxAllowed = unallocatedMaxAmount
          ? Math.min(balance, unallocatedMaxAmount)
          : balance;

        if (value && value > maxAllowed + 0.01) {
          return this.createError({
            message: `Cannot exceed ${formatPlainNumber(maxAllowed)} (Balance/Funds limit)`,
          });
        }
        return true;
      }),
    referenceCode: Yup.string().trim().nullable(), // Optional per DTO
    unallocatedPaymentId: Yup.string().nullable(),
  });

  const initialValues: PaymentFormValues = {
    paymentMethodId: "",
    amount: balance, // Default to full balance
    referenceCode: "",
    unallocatedPaymentId: null,
    unallocatedMaxAmount: null,
  };

  // Determine wording & visibility based on entity type
  const isPayableByUs = entityType === "Purchase Order";
  const title = isPayableByUs
    ? `Pay Supplier (${entityType} #${documentNumber})`
    : `Receive Payment (${entityType} #${documentNumber})`;
  const balanceLabel = isPayableByUs
    ? "Outstanding Balance to Pay"
    : "Total Outstanding Balance";

  return (
    <AppModal
      open={open}
      onClose={loading ? () => {} : onClose}
      title={title}
      width="sm"
    >
      <FormAlert message={error} severity="error" />
      <Formik
        initialValues={initialValues}
        validationSchema={paymentSchema}
        validateOnMount={true} // Crucial for disabled state on load
        onSubmit={(values) => {
          const payload: PaymentRequestDto = {
            paymentMethodId: values.paymentMethodId,
            amount: Number(values.amount),
            reference: values.referenceCode || undefined, // Map form field to DTO field
            unallocatedPaymentId: values.unallocatedPaymentId || undefined,
          };
          onSubmitPayment(entityId, payload);
        }}
      >
        {({
          values,
          setValues,
          isValid,
          dirty,
        }: FormikProps<PaymentFormValues>) => (
          <Form>
            <Stack spacing={2.5}>
              {/* BALANCE SUMMARY */}
              <Box
                sx={{
                  p: 2,
                  bgcolor: alpha(theme.palette.secondary.main, 0.05),
                  border: `1px solid ${alpha(theme.palette.secondary.main, 0.2)}`,
                  borderRadius: 1,
                }}
              >
                <Stack
                  direction="row"
                  sx={{ justifyContent: "space-between", alignItems: "center" }}
                >
                  <Typography variant="body2" color="text.secondary">
                    {balanceLabel}
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{ color: "secondary.main", fontWeight: "bold" }}
                  >
                    {formatCurrency(balance, currencyCode)}
                  </Typography>
                </Stack>
              </Box>

              {/* INTEGRATION BLOCK - Only for inbound payments if settings allow */}
              {!isPayableByUs && branchSettings?.paymentIntegration?.active && (
                <Box
                  sx={{
                    p: 2,
                    border: `1px dashed ${theme.palette.divider}`,
                    borderRadius: 1,
                  }}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", mb: 1, fontWeight: "bold" }}
                  >
                    ELECTRONIC PAYMENTS
                  </Typography>

                  {values.unallocatedPaymentId ? (
                    <Box>
                      <Chip
                        color="success"
                        icon={<CheckCircleIcon />}
                        label={`Linked: ${values.referenceCode} (Total: ${values.unallocatedMaxAmount})`}
                        onDelete={() => {
                          // Atomic unlink guarantees immediate re-validation
                          setValues(
                            {
                              ...values,
                              unallocatedPaymentId: null,
                              referenceCode: "",
                              unallocatedMaxAmount: null,
                              amount: balance,
                            },
                            true,
                          );
                        }}
                        sx={{ fontWeight: "bold", mb: 1 }}
                      />
                      <Typography
                        variant="caption"
                        color="success.main"
                        sx={{ display: "block" }}
                      >
                        * You can edit the &quot;Amount&quot; field below to
                        allocate a partial amount.
                      </Typography>
                    </Box>
                  ) : (
                    <Stack direction="row" spacing={2}>
                      {branchSettings.paymentIntegration.allowsMpesaStk && (
                        <Button
                          size="small"
                          variant="contained"
                          color="success"
                          startIcon={<MpesaIcon />}
                          onClick={() => setStkModalOpen(true)}
                        >
                          Send STK Push
                        </Button>
                      )}
                      <Button
                        size="small"
                        variant="outlined"
                        color="secondary"
                        startIcon={<LinkIcon />}
                        onClick={() => setAllocateModalOpen(true)}
                      >
                        Link Received Payment
                      </Button>
                    </Stack>
                  )}
                </Box>
              )}

              {/* STANDARD FORM FIELDS */}
              <FormSelectField
                name="paymentMethodId"
                label="Payment Method"
                options={pmOptions}
              />
              <FormTextField
                name="amount"
                label="Amount"
                type="number"
                // Kept fully editable so cashiers can record partial amounts
              />
              <FormTextField
                name="referenceCode"
                label="Reference Code / Transaction ID (Optional)"
                slotProps={{
                  input: {
                    readOnly: !!values.unallocatedPaymentId,
                  },
                }}
                sx={{
                  "& .MuiInputBase-input.Mui-readOnly": {
                    backgroundColor: alpha(
                      theme.palette.action.disabledBackground,
                      0.1,
                    ),
                    cursor: "not-allowed",
                  },
                }}
              />

              <SubmitButton
                title={
                  isPayableByUs
                    ? "Record Outbound Payment"
                    : "Record Received Payment"
                }
                loadingText="Processing..."
                loading={loading}
                disabled={
                  !isValid ||
                  (!dirty && !values.unallocatedPaymentId) ||
                  loading
                }
                startIcon={<PaymentIcon />}
                sx={{ mt: 1, py: 1.5 }}
              />
            </Stack>

            {/* --- SUB-MODALS --- */}
            {stkModalOpen && (
              <StkPushModal
                open={stkModalOpen}
                onClose={() => setStkModalOpen(false)}
                defaultAmount={values.amount}
                defaultPhone={getCustomerPhone()}
                referencePrefix={documentNumber}
              />
            )}

            {allocateModalOpen && (
              <AllocatePaymentModal
                open={allocateModalOpen}
                onClose={() => setAllocateModalOpen(false)}
                onSelect={(payment) => {
                  const paymentAmount = Number(payment.amount);
                  const allocatableAmount = Math.min(paymentAmount, balance);

                  const mpesaMethod = pmOptions.find((o) =>
                    o.label.toLowerCase().includes("mpesa"),
                  );

                  // Atomic update using setValues guarantees Formik updates perfectly
                  setValues(
                    {
                      ...values,
                      unallocatedPaymentId: payment.id,
                      referenceCode: payment.gatewayReceiptNumber,
                      unallocatedMaxAmount: paymentAmount,
                      amount: Number(allocatableAmount.toFixed(2)),
                      paymentMethodId: mpesaMethod
                        ? mpesaMethod.value
                        : values.paymentMethodId,
                    },
                    true,
                  );

                  setAllocateModalOpen(false);
                }}
              />
            )}
          </Form>
        )}
      </Formik>
    </AppModal>
  );
};

export default RecordPaymentModal;
